import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate, useParams } from "react-router-dom";
import { Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { searchPlugin } from "@react-pdf-viewer/search";
import {
  highlightPlugin,
  RenderHighlightTargetProps,
} from "@react-pdf-viewer/highlight";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

import {
  Button,
  IconButton,
  Divider as MuiDivider,
  Typography as MuiTypography,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Box, spacing, SpacingProps } from "@mui/system";
import {
  deleteFileByName,
  getDocumentsByUUID,
  deleteBookmarkFromDB,
} from "../../firebase/utils";
import { setFiles } from "../../redux/slices/main";
import { deleteFilesFromSearch } from "../../redux/slices/search";
import {
  setBookmarkDialogState,
  setPreUploadedBookmarks,
} from "../../redux/slices/bookmark";
import { RootState } from "../../redux/store";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import { Bookmark, File } from "../../types/app";

const Divider = styled(MuiDivider)(spacing);

const PDFButton = styled(Button)<{
  highlightTarget: RenderHighlightTargetProps;
}>`
  color: ${({ theme }) => theme.palette.secondary.contrastText};
  background: ${({ theme }) => theme.palette.primary.main};
  position: absolute;
  border-radius: 8px;
  left: ${(props) => props.highlightTarget.selectionRegion.left}%;
  top: ${(props) =>
    props.highlightTarget.selectionRegion.top +
    props.highlightTarget.selectionRegion.height}%;
  transform: translate(0, 8px);
  z-index: 1;
  &:hover {
    background: ${({ theme }) => theme.palette.secondary.main};
  }
`;

const DeleteButton = styled(IconButton)`
  margin-left: 20px;
`;

const FileContainer = styled.div<{ drawerOpen: boolean }>`
  height: calc(100vh - 220px);
  width: ${(props) => (props.drawerOpen ? `calc(100% - 350px)` : "100%")};
  overflow: auto;
`;

interface TypographyProps extends SpacingProps {
  component?: string;
}
const Typography = styled(MuiTypography)<TypographyProps>(spacing);

function FilePage() {
  const { pdfId } = useParams<{ pdfId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [disabled, setDisabled] = useState(false);
  const [isDocumentLoaded, setDocumentLoaded] = useState(false);
  const [buttonAppear, setButtonAppear] = useState(false);

  const state = useAppSelector((state: RootState) => state.main);
  const bookmarkState = useAppSelector((state: RootState) => state.bookmark);
  const files = state.files;
  const drawerOpen = state.queryDrawerOpen;
  const bookmarks = bookmarkState.bookmarks;
  const loading = state.loading;
  const currFile: File = files.find((file: File) => file.fileName === pdfId)!;

  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const searchPluginInstance = searchPlugin();
  const { highlight, setTargetPages } = searchPluginInstance;

  useEffect(() => {
    setTargetPages((targetPage) => targetPage.pageIndex === state.viewPage - 1);
    highlight(
      state.viewQuery.map((keyword) => ({
        keyword: keyword,
        matchCase: false,
      }))
    );
  }, [isDocumentLoaded, state.viewQuery, state.viewPage]);

  useEffect(() => {
    if (!loading && !files.find((file: File) => file.fileName === pdfId)) {
      navigate("/auth/404");
    }
  }, [pdfId, files, loading, navigate]);

  const bookmarkClick = (props: RenderHighlightTargetProps) => {
    dispatch(
      setPreUploadedBookmarks([
        {
          index: bookmarkState.bookmarks.length,
          pdfName: pdfId,
          page: props.selectionData?.startPageIndex! + 1,
          title: "",
          description: "",
          group: "",
          displayText: props.selectedText,
          searchText: props.selectionData?.divTexts.reduce((a: string[], b) => {
            if (b.textContent !== "") {
              return a.concat(b.textContent);
            }
            return a;
          }, []),
        } as Bookmark,
      ])
    );
    dispatch(setBookmarkDialogState(true));
    props.toggle();
    setButtonAppear(false);
  };

  const renderHighlightTarget = (props: RenderHighlightTargetProps) => {
    setButtonAppear(true);
    return (
      <>
        {buttonAppear && (
          <PDFButton
            highlightTarget={props}
            onClick={() => bookmarkClick(props)}
          >
            Bookmark Section
          </PDFButton>
        )}
      </>
    );
  };

  const highlightPluginInstance = highlightPlugin({ renderHighlightTarget });

  function handleDelete(pdfId: string) {
    setDisabled(true);
    const idx = files.findIndex((file: File) => file.fileName === pdfId);
    const bookmarksToDelete = bookmarks.filter(
      (bookmark) => bookmark.pdfName === pdfId
    );
    deleteFileByName(pdfId).then(async () => {
      await getDocumentsByUUID("files").then((documents: File[]) => {
        dispatch(setFiles(documents));
      });
      bookmarksToDelete.forEach((bookmark) => {
        deleteBookmarkFromDB(bookmark);
      });
      setDisabled(false);
      if (files.length === 1) {
        navigate("/");
      } else if (idx === files.length - 1) {
        navigate(`/${files[idx - 1].fileName}`);
      } else {
        navigate(`/${files[idx + 1].fileName}`);
      }
    });
    dispatch(deleteFilesFromSearch(pdfId));
  }

  return (
    <React.Fragment>
      <Box>
        <Box display="flex" sx={{ alignItems: "center" }}>
          <Typography
            variant="h3"
            gutterBottom
            display="inline"
            sx={{
              marginTop: "10px",
              overflowWrap: "break-word",
              maxWidth: `calc(100% - ${50}px)`,
            }}
          >
            {pdfId}
          </Typography>
          <DeleteButton
            disabled={disabled}
            onClick={() => handleDelete(pdfId as string)}
          >
            <DeleteIcon
              color={disabled ? "disabled" : "error"}
              fontSize="large"
            />
          </DeleteButton>
        </Box>
        <Divider my={6} />
        <FileContainer drawerOpen={drawerOpen}>
          {currFile !== undefined && (
            <Viewer
              fileUrl={currFile.url}
              plugins={[
                defaultLayoutPluginInstance,
                searchPluginInstance,
                highlightPluginInstance,
              ]}
              onDocumentLoad={() => setDocumentLoaded(!isDocumentLoaded)}
            />
          )}
        </FileContainer>
      </Box>
    </React.Fragment>
  );
}

export default FilePage;
